import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';

/**
 * 增强版 Three.js 资源管理器
 * 支持加载：模型(gltf/glb)、纹理(texture)、JSON(json)、音频(audio)
 * 提供：整体进度显示、单个资源进度、错误处理、资源缓存与释放
 */
class ResourceManager {
  constructor(audioListener) {
    // 加载器实例初始化
    this.loaders = {
      texture: new THREE.TextureLoader(),
      gltf: new GLTFLoader(),
      json: new THREE.FileLoader().setResponseType('json'),
      audio: new THREE.AudioLoader()
    };

    // DRACO 解码器配置（用于压缩GLTF模型，可选）
    this.dracoLoader = new DRACOLoader();
    this.dracoLoader.setDecoderPath('libs/draco/'); // 需自行放置DRACO解码器文件
    this.dracoLoader.setDecoderConfig({ type: 'js' });
    this.loaders.gltf.setDRACOLoader(this.dracoLoader);

    // 音频监听器（用于音频播放，由外部传入保证全局唯一）
    this.audioListener = audioListener || new THREE.AudioListener();

    // 资源缓存(核心)：key -> 资源实例/数据
    this.cache = new Map();
    // 加载队列(核心)：存储待加载的资源配置
    this.loadQueue = [];
    // 加载状态标识
    this.isLoading = false;
    // 回调函数存储
    this.callbacks = {
      onGlobalProgress: null, // 整体加载进度
      onSingleProgress: null, // 单个资源加载进度
      onComplete: null,       // 全部加载完成
      onError: null           // 加载错误
    };
  }

  /**
   * 添加资源到加载队列
   * @param {string} type 资源类型：gltf | texture | json | audio
   * @param {string} key 资源唯一标识（用于后续获取）
   * @param {string} url 资源路径（本地/网络）
   * @param {object} [options={}] 可选配置（如音频是否创建PositionalAudio）
   */
  add(type, key, url, options = {}) {
    const validTypes = ['gltf', 'texture', 'json', 'audio'];
    if (!validTypes.includes(type)) {
      console.error(`不支持的资源类型：${type}，可选类型：${validTypes.join(', ')}`);
      return;
    }
    if (this.cache.has(key)) {
      console.warn(`资源 ${key} 已存在于缓存，跳过添加`);
      return;
    }
    this.loadQueue.push({ type, key, url, options });
  }

  /**
   * 开始加载队列中的所有资源
   * @param {Function} [onGlobalProgress] 整体进度回调 (progress: number) => void
   * @param {Function} [onSingleProgress] 单个资源进度回调 (key: string, progress: number) => void
   * @param {Function} [onComplete] 完成回调 (cache: Map) => void
   * @param {Function} [onError] 错误回调 (error: Error, key: string) => void
   */
  loadAll(onGlobalProgress, onSingleProgress, onComplete, onError) {
    if (this.isLoading) {
      console.warn('已有加载任务正在进行，请勿重复调用');
      return;
    }
    if (this.loadQueue.length === 0) {
      onComplete?.(this.cache);
      return;
    }

    // 绑定回调函数
    this.callbacks.onGlobalProgress = onGlobalProgress;
    this.callbacks.onSingleProgress = onSingleProgress;
    this.callbacks.onComplete = onComplete;
    this.callbacks.onError = onError;

    this.isLoading = true;
    const total = this.loadQueue.length;
    let loaded = 0;

    // 遍历队列加载资源
    this.loadQueue.forEach(async (item) => {
      const { type, key, url, options } = item;
      try {
        const resource = await this._loadSingleResource(type, key, url, options);
        this.cache.set(key, resource);
        loaded++;
        // 计算整体进度并触发回调
        const globalProgress = (loaded / total) * 100;
        this.callbacks.onGlobalProgress?.(globalProgress);
        // 所有资源加载完成
        if (loaded === total) {
          this.isLoading = false;
          this.callbacks.onComplete?.(this.cache);
          this.loadQueue = []; // 清空队列
        }
      } catch (error) {
        this.isLoading = false;
        this.callbacks.onError?.(error, key);
        console.error(`资源 ${key} 加载失败：`, error);
      }
    });
  }

  /**
   * 加载单个资源的内部方法
   * @param {string} type 资源类型
   * @param {string} key 资源标识
   * @param {string} url 资源路径
   * @param {object} options 可选配置
   * @returns {Promise<any>} 资源实例/数据
   */
  _loadSingleResource(type, key, url, options) {
    return new Promise((resolve, reject) => {
      const loader = this.loaders[type];
      if (!loader) {
        reject(new Error(`未找到 ${type} 对应的加载器`));
        return;
      }

      // 加载器的 onLoad 回调
      const onLoad = (data) => {
        // 音频资源特殊处理：创建 Audio 或 PositionalAudio 实例
        if (type === 'audio') {
          const audio = options.isPositional 
            ? new THREE.PositionalAudio(this.audioListener) 
            : new THREE.Audio(this.audioListener);
          audio.setBuffer(data);
          resolve(audio);
        } else {
          resolve(data);
        }
      };

      // 加载器的 onProgress 回调
      const onProgress = (xhr) => {
        if (xhr.lengthComputable) {
          const progress = (xhr.loaded / xhr.total) * 100;
          this.callbacks.onSingleProgress?.(key, progress);
        }
      };

      // 加载器的 onError 回调
      const onError = (error) => {
        reject(error);
      };

      // 执行加载
      switch (type) {
        case 'gltf':
          loader.load(url, onLoad, onProgress, onError);
          break;
        case 'texture':
          loader.load(url, onLoad, onProgress, onError);
          break;
        case 'json':
          loader.load(url, onLoad, onProgress, onError);
          break;
        case 'audio':
          loader.load(url, onLoad, onProgress, onError);
          break;
        default:
          reject(new Error(`不支持的资源类型：${type}`));
      }
    });
  }

  /**
   * 获取已加载的资源
   * @param {string} key 资源标识
   * @returns {THREE.Texture | THREE.GLTF | object | THREE.Audio | null}
   */
  get(key) {
    return this.cache.get(key) || null;
  }

  /**
   * 释放指定资源
   * @param {string} key 资源标识
   */
  release(key) {
    const resource = this.cache.get(key);
    if (!resource) {
      console.warn(`资源 ${key} 不存在，无需释放`);
      return;
    }

    // 根据资源类型执行不同的释放逻辑
    if (resource instanceof THREE.Texture) {
      resource.dispose();
    } else if (resource.scene) { // GLTF模型
      resource.scene.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
          if (Array.isArray(obj.material)) {
            obj.material.forEach(mat => mat.dispose());
          } else {
            obj.material.dispose();
          }
        }
      });
    } else if (resource instanceof THREE.Audio || resource instanceof THREE.PositionalAudio) {
      resource.disconnect();
      resource.buffer?.dispose();
    }
    // JSON数据无需手动释放，由垃圾回收处理

    this.cache.delete(key);
    console.log(`资源 ${key} 已成功释放`);
  }

  /**
   * 释放所有资源
   */
  releaseAll() {
    this.cache.forEach((_, key) => this.release(key));
    this.cache.clear();
    console.log('所有资源已成功释放');
  }
}

// 导出单例（传入全局音频监听器，保证音频播放统一）
export const resourceManager = new ResourceManager(new THREE.AudioListener());

// 导出类本身，用于自定义实例
export default ResourceManager;
